import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import EntryComposer from "@/components/journal/EntryComposer";
import JournalContent from "@/components/journal/JournalContent";
import api from "@/lib/api";
import { toast } from "sonner";
import { timeAgo } from "@/lib/format";
import { Loader2, NotebookPen, Plus, Pencil, Trash2, ArrowUpRight } from "lucide-react";

/* Journal entries linked to one question, written from the question page. */
export default function NoteDrawer({ open, onOpenChange, question }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [composing, setComposing] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!question) return;
    setLoading(true);
    try {
      const { data } = await api.get("/journal", { params: { question_id: question.id } });
      const full = await Promise.all((data.entries || []).map((e) => api.get(`/journal/${e.id}`).then((r) => r.data)));
      setEntries(full);
      setComposing(full.length === 0);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [question]);

  useEffect(() => { if (open && question) { setEditing(null); load(); } }, [open, question, load]);

  const afterChange = () => { setComposing(false); setEditing(null); load(); };
  const remove = async (id) => {
    try { await api.delete(`/journal/${id}`); toast.success("Entry removed."); afterChange(); } catch { toast.error("Could not remove. Try again."); }
  };

  if (!question) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-xl" data-testid="note-drawer">
        <SheetHeader className="text-left">
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold"><NotebookPen className="h-3.5 w-3.5" /> Your Journal</div>
          <SheetTitle className="no-reftag font-serif text-2xl">{question.reference}</SheetTitle>
          <SheetDescription className="font-serif text-base italic text-muted-foreground">&ldquo;{question.question_kjv}&rdquo;</SheetDescription>
        </SheetHeader>

        <div className="mt-6 flex-1 space-y-4">
          {loading ? (
            <div className="flex h-40 items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <>
              {entries.map((e) => editing === e.id ? (
                <EntryComposer key={e.id} entry={e} compact autoFocus onSaved={afterChange} onCancel={() => setEditing(null)} />
              ) : (
                <div key={e.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`my-entry-${e.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{e.title || "Entry"} · {timeAgo(e.updated_at)}</span>
                    <div className="flex gap-1">
                      <button data-testid={`edit-entry-${e.id}`} onClick={() => { setComposing(false); setEditing(e.id); }} className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`delete-entry-${e.id}`} onClick={() => remove(e.id)} className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <JournalContent doc={e.doc} className="mt-3 text-[15px]" testId={`entry-content-${e.id}`} />
                </div>
              ))}
              {composing ? (
                <EntryComposer questionId={question.id} compact autoFocus={entries.length > 0} onSaved={afterChange} onCancel={entries.length > 0 ? () => setComposing(false) : undefined} />
              ) : (
                <Button data-testid="add-entry-button" variant="outline" onClick={() => { setEditing(null); setComposing(true); }} className="w-full rounded-full border-dashed">
                  <Plus className="mr-2 h-4 w-4" /> Add another entry
                </Button>
              )}
            </>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <Link to="/journal" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="drawer-open-journal">Open full journal <ArrowUpRight className="h-3.5 w-3.5" /></Link>
          <Button variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)}>Close</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
